import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import type { FieldErrors, FieldValues, UseFormMethods } from "react-hook-form";
import { CountiesDropdown } from "./CountiesDropdown";
import { IUserAddress } from "@interface/customerDetails/IAddress";
import { Grid } from "@mui/material";
import { SimpleEirCode } from "@features/eircode/SimpleEircode/SimpleEirCode";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";

interface IParams<TFieldValues extends FieldValues> {
    addressForm: UseFormMethods<TFieldValues> & Partial<UseFormMethods>;
    prefix?: string;
}

export const Address = <TFieldValues extends FieldValues>(props: IParams<TFieldValues>) => {
    const { addressForm, prefix } = props;
    const { errors, register, control } = addressForm;

    const formErrors = (prefix ? errors[prefix] : errors) as FieldErrors<IUserAddress>;
    const namePrefix = prefix ? `${prefix}.` : "";
    const idPrefix = prefix ? `${prefix}-` : "";

    const { t } = useTranslation(["address", "validation"]);

    const addressLine1 = useWatch<string>({ name: `${namePrefix}addressLine1`, control: addressForm.control });
    const addressLine2 = useWatch<string>({ name: `${namePrefix}addressLine2`, control: addressForm.control });
    const addressLine3 = useWatch<string>({ name: `${namePrefix}addressLine3`, control: addressForm.control });
    const town = useWatch<string>({ name: `${namePrefix}town`, control: addressForm.control });

    return (
        <Grid columnSpacing={4} container rowSpacing={2}>
            <Grid item xs={12}>
                <SimpleEirCode addressForm={addressForm} isRequired prefix={prefix} />
            </Grid>
            <Grid item xs={12}>
                <LabeledTextField
                    InputLabelProps={{ shrink: Boolean(addressLine1) }}
                    error={Boolean(formErrors?.addressLine1)}
                    fullWidth
                    helperText={formErrors?.addressLine1?.message}
                    id={`${idPrefix}addressLine1`}
                    inputRef={register({
                        required: t("validation:required"),
                        maxLength: { value: 50, message: t("validation:tooManyCharacters", { count: 50 }) },
                        minLength: { value: 2, message: t("validation:notEnoughCharacters", { count: 2 }) },
                    })}
                    isRequired
                    labelText={t("address:addressLine1")}
                    name={`${namePrefix}addressLine1`}
                    placeholder={t("address:addressLine1Placeholder")}
                />
            </Grid>
            <Grid item xs={12}>
                <LabeledTextField
                    InputLabelProps={{ shrink: Boolean(addressLine2) }}
                    error={Boolean(formErrors?.addressLine2)}
                    fullWidth
                    helperText={formErrors?.addressLine2?.message}
                    id={`${idPrefix}addressLine2`}
                    inputRef={register({
                        maxLength: { value: 50, message: t("validation:tooManyCharacters", { count: 50 }) },
                    })}
                    labelText={t("address:addressLine2")}
                    name={`${namePrefix}addressLine2`}
                    placeholder={t("address:addressLine2Placeholder")}
                />
            </Grid>
            <Grid item xs={12}>
                <LabeledTextField
                    InputLabelProps={{ shrink: Boolean(addressLine3) }}
                    error={Boolean(formErrors?.addressLine3)}
                    fullWidth
                    helperText={formErrors?.addressLine3?.message}
                    id={`${idPrefix}addressLine3`}
                    inputRef={register({
                        maxLength: { value: 50, message: t("validation:tooManyCharacters", { count: 50 }) },
                    })}
                    labelText={t("address:addressLine3")}
                    name={`${namePrefix}addressLine3`}
                    placeholder={t("address:addressLine3Placeholder")}
                />
            </Grid>
            <Grid item md={6} xs={12}>
                <LabeledTextField
                    InputLabelProps={{ shrink: Boolean(town) }}
                    error={Boolean(formErrors?.town)}
                    fullWidth
                    helperText={formErrors?.town?.message}
                    id={`${idPrefix}town`}
                    inputRef={register({
                        required: t("validation:required"),
                    })}
                    isRequired
                    labelText={t("address:town")}
                    name={`${namePrefix}town`}
                    placeholder={t("address:townPlaceholder")}
                />
            </Grid>
            <Grid item md={6} xs={12}>
                <CountiesDropdown control={control} error={formErrors?.county} prefix={prefix} />
            </Grid>
        </Grid>
    );
};
